import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { InboxConversation } from '@/types/residents/marketplaceListing';
import { InboxConversationCard } from './InboxConversationCard';
import { useGetListingConversationsQuery } from '@/store/api';

type NavigationProp = StackNavigationProp<RootStackParamList>;

interface ListingConversationsListProps {
  listingId: string;
  conversations?: InboxConversation[];
}

/**
 * Displays a list of buyer conversations for a seller's listing.
 * Used in MyListingItemDetailScreen to show all buyers who have messaged about the item.
 */
export const ListingConversationsList: React.FC<ListingConversationsListProps> = ({
  listingId,
  conversations: externalConversations,
}) => {
  const navigation = useNavigation<NavigationProp>();

  // Fetch conversations if not provided externally
  const { 
    data: fetchedConversations = [], 
    isLoading,
  } = useGetListingConversationsQuery(listingId, {
    skip: !!externalConversations,
  });

  const conversations = externalConversations || fetchedConversations;

  const handleConversationPress = (conversation: InboxConversation) => {
    navigation.navigate('InboxConversation', { 
      conversationId: conversation.id,
    });
  };

  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      backgroundColor: mc.cardBackground,
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: mc.dividerGray,
    },
    loadingContainer: {
      padding: 40,
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderBottomWidth: 1,
      borderBottomColor: mc.dividerGray,
    },
    headerTitleContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    headerTitle: {
      fontSize: 14,
      fontWeight: '600',
      color: mc.textPrimary,
      lineHeight: 21,
    },
    headerSubtitle: {
      fontSize: 12,
      fontWeight: '400',
      color: mc.textSecondary,
      lineHeight: 18,
      marginTop: 2,
    },
    unreadBadge: {
      backgroundColor: mc.productivityOrange,
      borderRadius: 10,
      minWidth: 20,
      height: 20,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 6,
    },
    unreadBadgeText: {
      fontSize: 12,
      fontWeight: '600',
      color: mc.textPrimary,
      lineHeight: 16,
    },
    listContainer: {
      // InboxConversationCard already has its own padding
    },
}), [mc]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Buyer Messages</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={mc.accentRed} />
        </View>
      </View>
    );
  }

  if (!conversations || conversations.length === 0) {
    return null;
  }

  const unreadCount = conversations.filter(c => c.isUnread).length;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Buyer Messages</Text>
          {unreadCount > 0 && (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadBadgeText}>{unreadCount}</Text>
            </View>
          )}
        </View>
        <Text style={styles.headerSubtitle}>
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </Text>
      </View>

      {/* Conversations List */}
      <View style={styles.listContainer}>
        {conversations.map((conversation) => (
          <InboxConversationCard
            key={conversation.id}
            conversation={conversation}
            onPress={() => handleConversationPress(conversation)}
          />
        ))}
      </View>
    </View>
  );
};

